
import React, { useEffect, useContext, useState } from "react";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import { useImmerReducer } from "use-immer";
import {
    Grid,
    Typography,
    Button,
    TextField,
    Snackbar,
    Tooltip,
    IconButton,
    Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";

function ProfileUpdate({ userProfile, onProfileUpdated }) {
    const { t } = useTranslation();
    const { config, environment } = require("../../index");
    const navigate = useNavigate();
    const GlobalState = useContext(StateContext);

    const userId = GlobalState.userId;

    const initialState = {
        companyNameValue: userProfile.companyName || "",
        phoneNumberValue: userProfile.phoneNumber || "",
        bioValue: userProfile.bio || "",
        uploadedPicture: [],
        profilePictureValue: userProfile.profilePic || null,
        sendRequest: 0,
        openSnack: false,
        disabledUpdateBtn: false,
        snackMessage: "",
        snackSeverity: "success"
    };

    function reducer(draft, action) {
        switch (action.type) {
            case "catchCompanyNameChange":
                draft.companyNameValue = action.value;
                break;
            case "catchPhoneNumberChange":
                draft.phoneNumberValue = action.value;
                break;
            case "catchBioChange":
                draft.bioValue = action.value;
                break;
            case "catchUploadedPicture":
                draft.uploadedPicture = action.files;
                draft.profilePictureValue = action.files[0];
                break;
            case "changeSendRequest":
                draft.sendRequest++;
                break;
            case "openSnack":
                draft.openSnack = true;
                draft.snackMessage = action.message;
                draft.snackSeverity = action.severity || "success";
                break;
            case "closeSnack":
                draft.openSnack = false;
                break;
            case "disableUpdateBtn":
                draft.disabledUpdateBtn = true;
                break;
            case "enableUpdateBtn":
                draft.disabledUpdateBtn = false;
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(reducer, initialState);

    // Handle profile update - ONLY when sendRequest changes
    useEffect(() => {
        if (!state.sendRequest) return;
        const source = Axios.CancelToken.source();

        async function updateProfile() {
            const formData = new FormData();
            formData.append("company_name", state.companyNameValue);
            formData.append("phone_number", state.phoneNumberValue);
            formData.append("bio", state.bioValue);
            formData.append("seller", userId);

            // Only append if it's a new file
            if (state.profilePictureValue instanceof File) {
                formData.append("profile_picture", state.profilePictureValue);
            }

            try {
                dispatch({ type: "disableUpdateBtn" });
                const endpoint = config[environment].getProfileUpdateEndpoint(userId);
                await Axios.patch(endpoint, formData, {
                    headers: { Authorization: `Bearer ${GlobalState.jwtAccessToken}` },
                    cancelToken: source.token
                });
                dispatch({ type: "openSnack", message: t('Administration.ProfileUpdated'), severity: "success" });
                dispatch({ type: "enableUpdateBtn" });

                // Call parent's refetch function if provided
                if (onProfileUpdated && typeof onProfileUpdated === 'function') {
                    setTimeout(() => {
                        onProfileUpdated();
                    }, 500);
                }
            } catch (err) {
                console.error("[ProfileUpdate] Update failed:", err);
                dispatch({ type: "enableUpdateBtn" });
                dispatch({ type: "openSnack", message: "Hiba történt a frissítés során.", severity: "error" });
            }
        }

        updateProfile();
        return () => source.cancel();
    }, [state.sendRequest]);

    function handleSubmit(e) {
        e.preventDefault();
        dispatch({ type: "changeSendRequest" });
    }

    // Handle profile deletion
    async function handleDelete() {
        if (!window.confirm(t('Administration.DeleteConfirm', 'Biztos benne, hogy törölni szeretné a profilját?'))) return;
        try {
            const deleteEndpoint = config[environment].deleteProfileEndpoint(userId);
            const response = await Axios.delete(deleteEndpoint, {
                headers: { Authorization: `Bearer ${GlobalState.jwtAccessToken}` }
            });
            if (response.status === 204) {
                dispatch({ type: "openSnack", message: t('Administration.ProfileDeleted', 'Profil sikeresen törölve!'), severity: "success" });
                setTimeout(() => navigate("/"), 1500);
            }
        } catch (err) {
            console.error("[ProfileUpdate] Delete failed:", err);
            dispatch({ type: "openSnack", message: t('Administration.GenericError'), severity: "error" });
        }
    }

    function ProfilePictureDisplay() {
        if (state.profilePictureValue instanceof File) {
            return (
                <Grid item sx={{ mt: 1, mx: "auto", textAlign: "center" }}>
                    <img
                        src={URL.createObjectURL(state.profilePictureValue)}
                        alt="profile preview"
                        style={{ width: "5rem", height: "5rem", objectFit: "cover", borderRadius: "50%" }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {state.profilePictureValue.name}
                    </Typography>
                </Grid>
            );
        } else if (state.profilePictureValue) {
            return (
                <Grid item sx={{ mt: 1, mx: "auto", textAlign: "center" }}>
                    <img
                        src={state.profilePictureValue}
                        alt="profile"
                        style={{ width: "5rem", height: "5rem", objectFit: "cover", borderRadius: "50%" }}
                    />
                </Grid>
            );
        }
        return null;
    }

    return (
        <Grid container justifyContent="center" sx={{ mt: 5 }}>
            <Grid item xs={11} sm={10} md={8} lg={6} sx={{ border: "2px solid black", p: { xs: 1.5, md: 3 } }}>
                <form onSubmit={handleSubmit}>
                    <Grid container justifyContent="flex-end">
                        <Tooltip title={t('Administration.DeleteTooltip', 'Felhasználói adatok törlése')}>
                            <IconButton aria-label="delete" onClick={handleDelete} sx={{ color: "red" }}>
                                <DeleteIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </Grid>

                    <Grid container justifyContent="center" sx={{ mb: 2 }}>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>{t('Administration.ProfileData', 'PROFILADATOK')}</Typography>
                    </Grid>

                    <TextField
                        fullWidth
                        label={t('Administration.CompanyName', 'Cégnév') + "*"}
                        value={state.companyNameValue}
                        onChange={e => dispatch({ type: "catchCompanyNameChange", value: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label={t('Administration.PhoneNumber', 'Telefonszám') + "*"}
                        value={state.phoneNumberValue}
                        onChange={e => dispatch({ type: "catchPhoneNumberChange", value: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label={t('Administration.Bio', 'Bemutatkozás')}
                        multiline
                        rows={7}
                        value={state.bioValue}
                        onChange={e => dispatch({ type: "catchBioChange", value: e.target.value })}
                        sx={{ mb: 2 }}
                    />

                    {ProfilePictureDisplay()}

                    <Button
                        variant="contained"
                        component="label"
                        fullWidth
                        sx={{ mb: 2, bgcolor: "green" }}
                    >
                        {t('Administration.ProfilePicture', 'Profilkép')}
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={e =>
                                dispatch({ type: "catchUploadedPicture", files: e.target.files })
                            }
                        />
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={state.disabledUpdateBtn}
                    >
                        {t('Administration.UpdateProfile', 'Profil frissítése')}
                    </Button>
                </form>

                <Snackbar
                    open={state.openSnack}
                    autoHideDuration={3000}
                    onClose={() => dispatch({ type: "closeSnack" })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert severity={state.snackSeverity} sx={{ width: "100%" }}>
                        {state.snackMessage}
                    </Alert>
                </Snackbar>
            </Grid>
        </Grid>
    );
}

export default ProfileUpdate;